import type { SortDirection } from '@backend/schemas'
import { useSearchParams } from 'react-router-dom'

/**
 * Page, sort, search and filters for a paginated list, kept in the URL so they survive reloads,
 * back navigation and can be shared as links.
 */
export function useListParams<F extends string>(defaults: { sortField: F; sortDir: SortDirection }) {
  const [searchParams, setSearchParams] = useSearchParams()

  const params = {
    page: Number(searchParams.get('page')) || 1,
    sortField: (searchParams.get('sortField') as F) || defaults.sortField,
    sortDir: (searchParams.get('sortDir') as SortDirection) || defaults.sortDir,
    search: searchParams.get('search') ?? '',
    workshop: searchParams.get('workshop') ?? '',
  }

  // null removes the param. Changing anything but the page goes back to page 1.
  const update = (patch: Record<string, string | null>) => {
    setSearchParams(
      prev => {
        const next = new URLSearchParams(prev)
        Object.entries(patch).forEach(([key, value]) => {
          if (value === null || value === '') next.delete(key)
          else next.set(key, value)
        })
        if (!('page' in patch)) next.delete('page')
        return next
      },
      { replace: true },
    )
  }

  // Clicking the active column flips direction, a new column starts in its default direction
  const setSort = (field: F, defaultDir: SortDirection = 'desc') => {
    const sortDir = params.sortField === field ? (params.sortDir === 'asc' ? 'desc' : 'asc') : defaultDir
    update({ sortField: field, sortDir })
  }

  return { params, update, setSort, searchParams }
}
